import getConfig from "next/config";
import { subDays } from "date-fns";
import { print, stripIgnoredCharacters } from "graphql";
import gql from "graphql-tag";
import type { DocumentNode } from "graphql/language/ast";
import { radioProfile } from "@constants/consts";
import { Playout, Schedule, Station, Track, TrackHistoryItem } from "./dataModels";

const {
  publicRuntimeConfig: { audioApiEndpoint, audioXApiKey, appsyncApiKey, appsyncHost },
} = getConfig();

const RadioScheduleQuery = gql`
  query RadioScheduleQuery($stationSlug: String, $startDate: AWSDateTime, $endDate: AWSDateTime) {
    station(slug: $stationSlug) {
      schedules(broadcastStartDate: $startDate, broadcastEndDate: $endDate) {
        broadcastStartDate
        broadcastEndDate
        id
        show {
          title
          slug
          images {
            uri
            type
            imageType
            variant {
              slug
            }
          }
        }
      }
    }
  }
`;

const GetPlayouts = gql`
  query GetPlayouts($slug: String!, $nextToken: String, $startDate: AWSDateTime) {
    station(slug: $slug) {
      getPlayouts(nextToken: $nextToken, limit: 100, broadcastStartDate: $startDate) {
        playouts {
          id
          broadcastDate
          track {
            isrc
            id
            title
            artistName
            media {
              id
              source
              type
              title
              uri
            }
            images {
              title
              uri
            }
          }
        }
        nextToken
      }
    }
  }
`;

const getStation = gql`
  query GetStation($slug: String!) {
    station(slug: $slug) {
      id
      type
      title
      shortTitle
      slug
      media {
        type
        uri
        source
      }
      images {
        uri
        type
        description
        imageType
      }
      references {
        ... on ExternalEmbed {
          embedId
        }
      }
    }
  }
`;

const onStationEvent = `subscription onStationEvent($profile: String!, $slug: String!) {
    stationEvent(slug: $slug) {
      slug
      id
      title
      playouts(profile: $profile) {
        type
        broadcastDate
        track {
          id
          isrc
          title
          artistName
          images {
            uri
            imageType
            title
          }
        }
      }
    }
}`;

const CurrentTrackQuery = gql`
  query CurrentTrackQuery($stationSlug: String!, $profile: String!) {
    station(slug: $stationSlug) {
      playouts(profile: $profile) {
        track {
          id
          title
          artistName
          images {
            uri
            imageType
            title
          }
        }
      }
    }
  }
`;

export async function fetchCurrentTrack(stationSlug: string): Promise<Track | null> {
  const { data } = await fetchAudioData(CurrentTrackQuery, {
    stationSlug,
    profile: radioProfile,
  });

  if (data?.station && Array.isArray(data.station.playouts) && data.station.playouts.length > 0) {
    const trackData = data.station.playouts[0].track;
    if (trackData) {
      return new Track(trackData);
    }
  }

  return null;
}

export default async function fetchAudioData(
  query: DocumentNode,
  variables: {
    stationSlug?: string;
    slug?: string;
    startDate?: Date;
    endDate?: Date;
    nextToken?: string | null;
    skip?: number;
    profile?: string;
  }
): Promise<{ data: { station: UniversalApiStation } | null }> {
  const queryParams = new URLSearchParams({
    query: stripIgnoredCharacters(print(query)),
    variables: JSON.stringify(variables),
  });

  const response = await fetch(`${audioApiEndpoint}?${queryParams.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Api-Key": audioXApiKey,
      Profile: radioProfile,
    },
  });
  const { data, errors } = await response.json();

  if (errors?.length) {
    throw new Error(errors[0].message);
  }

  return { data };
}

export async function fetchRadioSchedule(startDate: Date, endDate: Date, stationSlug?: string): Promise<Schedule[]> {
  if (!stationSlug) return [];

  return fetchAudioData(RadioScheduleQuery, {
    stationSlug,
    startDate,
    endDate,
  }).then(({ data }) => {
    if (!data?.station.schedules) return [];
    return data.station.schedules.map((schedule: UniversalApiRadioSchedule) => new Schedule(schedule));
  });
}

export async function fetchStationMetadata(slug: string): Promise<Station | null> {
  return fetchAudioData(getStation, { slug }).then(({ data }) => {
    if (!data?.station) return null;
    return new Station(data.station);
  });
}

export async function fetchPlayouts(
  slug: string,
  nextToken: string | null,
  skip: number
): Promise<{ items: Playout[]; nextToken: string }> {
  const startDate = subDays(new Date(), 1);

  return fetchAudioData(GetPlayouts, { slug, nextToken, startDate, skip }).then(({ data }) => {
    if (!data?.station.getPlayouts) return { items: [], nextToken: "" };
    return {
      items: data?.station.getPlayouts.playouts.map((playout: UniversalApiPlayout) => new Playout(playout)),
      nextToken: data.station.getPlayouts.nextToken,
    };
  });
}

export const getTrackHistory = async ({
  slug,
  itemLimitPerPage,
  maxItems = Infinity,
}: {
  slug?: string;
  itemLimitPerPage: number;
  maxItems?: number;
}): Promise<TrackHistoryItem[][]> => {
  if (!slug) return [];
  const endDate = new Date();
  const startDate = subDays(endDate, 1);

  const [showResult, playoutResult] = await Promise.all([
    fetchRadioSchedule(startDate, endDate, slug),
    fetchPlayouts(slug, null, 100),
  ]);

  const playouts = playoutResult.items;
  let token = playoutResult.nextToken;

  while (token && playouts.length < maxItems) {
    const { items: playoutItems, nextToken } = await fetchPlayouts(slug, token, 100);
    token = nextToken;
    playouts.push(...playoutItems);
  }

  const items = [...showResult, ...playouts].sort((a, b) => b.broadcastDate.getTime() - a.broadcastDate.getTime());

  const pages: TrackHistoryItem[][] = [];
  while (items.length) {
    pages.push(items.splice(0, itemLimitPerPage));
  }
  return pages;
};

function getWebsocketUrl() {
  const header = Buffer.from(
    JSON.stringify({
      host: appsyncHost,
      "x-api-key": appsyncApiKey,
    })
  ).toString("base64");
  const payload = btoa(JSON.stringify({}));
  const url = `wss://${appsyncHost}/graphql/realtime?header=${header}&payload=${payload}&protocol=graphql-ws`;

  return url;
}

export class StationSubscription {
  websocket: WebSocket;
  onTrackEvent: (track: Track) => void;

  constructor(slug: string, onTrackEvent: (track: Track) => void) {
    const url = getWebsocketUrl();
    this.websocket = new WebSocket(url, ["graphql-ws"]);
    this.websocket.onopen = () => {
      this.websocket.send(
        JSON.stringify({
          type: "connection_init",
        })
      );
    };

    this.websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      switch (message.type) {
        case "connection_ack":
          this.startSubscription(slug);
          break;
        case "error":
          console.error(message);
          break;
        case "data":
          const trackData = message.payload.data.stationEvent.playouts[0]?.track;

          if (trackData) {
            const track = new Track(trackData);
            onTrackEvent(track);
          }
          break;
      }
    };
  }

  private startSubscription(slug: string) {
    const subscribeMessage = {
      id: window.crypto.randomUUID(),
      type: "start",
      payload: {
        data: JSON.stringify({
          query: onStationEvent,
          variables: { profile: radioProfile, slug },
        }),
        extensions: {
          authorization: {
            "x-api-key": appsyncApiKey,
            host: appsyncHost,
          },
        },
      },
    };
    this.websocket.send(JSON.stringify(subscribeMessage));
  }

  cleanUp() {
    this.websocket.close();
  }
}
